import React, { useRef, useState } from "react";
import { extractAmountFromReceipt } from "../../services/receiptOcrService";

/**
 * Componente para subir o tomar foto de un recibo y extraer el monto con OCR.
 * @param {boolean} autoScan - Si true, escanea automáticamente al subir (sin botón "Reconocer")
 */
const ReceiptScanner = ({ onAmountExtracted, onError, disabled, autoScan = false }) => {
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [progress, setProgress] = useState(0);

  const runOcr = async (file) => {
    setIsScanning(true);
    setProgress(0);
    try {
      const { amount, currency } = await extractAmountFromReceipt(file, (p) => setProgress(p));
      if (amount !== null) {
        onAmountExtracted?.({ amount, currency });
      } else {
        onError?.("No se pudo detectar un monto en la imagen. Intenta con otra foto más clara.");
      }
    } catch (err) {
      console.error("Error en OCR:", err);
      onError?.("Error al procesar la imagen. Intenta de nuevo.");
    } finally {
      setIsScanning(false);
      setProgress(0);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    setImagePreview({ url, file });
    setProgress(0);
    e.target.value = "";
    if (autoScan) runOcr(file);
  };

  const handleScan = async () => {
    if (!imagePreview?.file) return;
    await runOcr(imagePreview.file);
  };

  const handleClear = () => {
    if (imagePreview?.url) URL.revokeObjectURL(imagePreview.url);
    setImagePreview(null);
    setProgress(0);
    galleryInputRef.current && (galleryInputRef.current.value = "");
    cameraInputRef.current && (cameraInputRef.current.value = "");
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => galleryInputRef.current?.click()}
          disabled={disabled || isScanning}
          className="flex-1 px-4 py-3 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 text-blue-700 font-medium rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          Subir imagen
        </button>
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          disabled={disabled || isScanning}
          className="flex-1 px-4 py-3 bg-green-50 hover:bg-green-100 border-2 border-green-200 text-green-700 font-medium rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          </svg>
          Tomar foto
        </button>
      </div>

      {imagePreview && (
        <div className="rounded-xl border-2 border-gray-200 overflow-hidden bg-gray-50">
          <div className="relative">
            <img
              src={imagePreview.url}
              alt="Vista previa del recibo"
              className="w-full max-h-48 object-contain"
            />
            {isScanning && (
              <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin mb-2" />
                <span className="text-white font-medium">Escaneando... {progress}%</span>
              </div>
            )}
          </div>
          {!autoScan && (
            <div className="p-3 flex gap-2">
              <button
                type="button"
                onClick={handleScan}
                disabled={disabled || isScanning}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
              >
                {isScanning ? "Procesando..." : "Reconocer monto"}
              </button>
              <button
                type="button"
                onClick={handleClear}
                disabled={disabled || isScanning}
                className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Quitar
              </button>
            </div>
          )}
          {autoScan && (
            <div className="p-3">
              <button
                type="button"
                onClick={handleClear}
                disabled={disabled || isScanning}
                className="w-full px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors"
              >
                Cambiar imagen
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReceiptScanner;
